import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import { Send, Paperclip, Mic, StopCircle, X, FileText, Image, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface AttachedFile {
  id: string;
  file: File;
  preview?: string;
}

interface ChatInputProps {
  onSend: (message: string, files?: AttachedFile[]) => void;
  isStreaming?: boolean;
  onStop?: () => void;
  placeholder?: string;
  disabled?: boolean;
  enableFileUpload?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

export function ChatInput({
  onSend,
  isStreaming,
  onStop,
  placeholder = '메시지를 입력하세요...',
  disabled,
  enableFileUpload = true,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [message]);

  // Cleanup file previews on unmount
  useEffect(() => {
    return () => {
      attachedFiles.forEach((f) => {
        if (f.preview) {
          URL.revokeObjectURL(f.preview);
        }
      });
    };
  }, [attachedFiles]);

  const handleSubmit = () => {
    if ((message.trim() || attachedFiles.length > 0) && !disabled && !isStreaming) {
      onSend(message.trim(), attachedFiles.length > 0 ? attachedFiles : undefined);
      setMessage('');
      setAttachedFiles([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // IME 조합 중에는 Enter 키 이벤트 무시 (한글, 일본어 등 입력 시 중복 전송 방지)
    if (e.nativeEvent.isComposing || e.keyCode === 229) {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      addFiles(Array.from(files));
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addFiles = (files: File[]) => {
    const validFiles = files.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`파일 ${file.name}이(가) 최대 크기 10MB를 초과합니다`);
        return false;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type) && !file.name.endsWith('.md')) {
        console.warn(`파일 형식 ${file.type}은(는) 지원되지 않습니다`);
        return false;
      }
      return true;
    });

    const newAttachedFiles: AttachedFile[] = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    }));

    setAttachedFiles((prev) => [...prev, ...newAttachedFiles]);
  };

  const removeFile = (fileId: string) => {
    setAttachedFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="h-4 w-4" />;
    }
    if (file.type === 'application/pdf' || file.type.includes('document')) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div
      className={cn('border-t bg-background p-4', dragActive && 'ring-2 ring-primary ring-inset')}
      onDragEnter={enableFileUpload ? handleDrag : undefined}
      onDragLeave={enableFileUpload ? handleDrag : undefined}
      onDragOver={enableFileUpload ? handleDrag : undefined}
      onDrop={enableFileUpload ? handleDrop : undefined}
    >
      <div className="mx-auto max-w-3xl">
        {/* Attached Files Preview */}
        {attachedFiles.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {attachedFiles.map((attachedFile) => (
              <Badge
                key={attachedFile.id}
                variant="secondary"
                className="flex items-center gap-2 py-1 pl-2 pr-1"
              >
                {attachedFile.preview ? (
                  <img
                    src={attachedFile.preview}
                    alt={attachedFile.file.name}
                    className="h-6 w-6 rounded object-cover"
                  />
                ) : (
                  getFileIcon(attachedFile.file)
                )}
                <span className="max-w-[150px] truncate text-xs">
                  {attachedFile.file.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({formatFileSize(attachedFile.file.size)})
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 rounded-full hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => removeFile(attachedFile.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
        )}

        <div className="relative flex items-end gap-2">
          <div className="relative flex-1">
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                dragActive
                  ? '여기에 파일을 드롭하세요...'
                  : attachedFiles.length > 0
                    ? '이 파일에 대한 메시지를 추가하세요...'
                    : placeholder
              }
              disabled={disabled || isStreaming}
              className="min-h-[52px] resize-none pr-24"
              rows={1}
            />
            <div className="absolute bottom-2 right-2 flex items-center gap-1">
              {enableFileUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ALLOWED_FILE_TYPES.join(',')}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={disabled}
                    onClick={() => fileInputRef.current?.click()}
                    title="파일 첨부"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={disabled}
                title="음성 입력 (준비 중)"
              >
                <Mic className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {isStreaming ? (
            <Button
              onClick={onStop}
              variant="destructive"
              size="icon"
              className="h-[52px] w-[52px] shrink-0"
            >
              <StopCircle className="h-5 w-5" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={(!message.trim() && attachedFiles.length === 0) || disabled}
              size="icon"
              className={cn(
                'h-[52px] w-[52px] shrink-0 transition-colors',
                message.trim() || attachedFiles.length > 0
                  ? 'bg-primary hover:bg-primary/90'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              <Send className="h-5 w-5" />
            </Button>
          )}
        </div>

        <p className="mt-2 text-center text-xs text-muted-foreground">
          {enableFileUpload
            ? 'Enter를 눌러 전송, Shift+Enter로 줄바꿈. 파일을 드래그 앤 드롭하거나 클립 아이콘을 클릭하세요.'
            : 'Enter를 눌러 전송, Shift+Enter로 줄바꿈'}
        </p>
      </div>
    </div>
  );
}
